import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col } from 'react-bootstrap';

import { ValidationState } from '@models/upload';
import { ErrorDetails } from '@utils';

import { ReactComponent as UploadStep1Logo } from '@assets/images/UploadConfirmationDialog_Step1.svg';
import { ReactComponent as UploadStep2Logo } from '@assets/images/UploadConfirmationDialog_Step2.svg';
import { ReactComponent as UploadStep3Logo } from '@assets/images/UploadConfirmationDialog_Step3.svg';

import UploadError from '../Error/Error';
import ValidationStatus from './ValidationStatus';
import ConfirmModalDialog from '../../confirm/ConfirmModalDialog';
import './Validating.scss';

export interface IValidatingStateProps {
  errors: ErrorDetails[];
  uploading?: boolean;
  validationStatus: ValidationState;
  uploadCancelled?: boolean;
  runFolderValidationStatus?: ValidationState;
}
export interface IValidatingDispatchProps {
  upload: () => void;
}

const Validating: React.FC<IValidatingStateProps & IValidatingDispatchProps> =
  ({
    errors,
    validationStatus,
    uploading,
    uploadCancelled,
    runFolderValidationStatus,
    upload,
  }) => {
    const { t } = useTranslation();
    const [showDialog, setShowDialog] = React.useState(false);
    const uploadChecklistSet = [
      {
        img: UploadStep1Logo,
        titleToken: 'upload_uploadChecklist_runFolder',
        columnCls: 'p-2 upload-logo-divider',
      },
      {
        img: UploadStep2Logo,
        titleToken: 'upload_uploadChecklist_experimentList',
        columnCls: 'p-2 upload-logo-divider',
      },
      {
        img: UploadStep3Logo,
        titleToken: 'upload_uploadChecklist_credentials',
        columnCls: 'p-2',
      },
    ];
    const onUpload = (e: React.MouseEvent) => {
      e.preventDefault();
      setShowDialog(true);
    };
    const confirmedUpload = () => {
      setShowDialog(false);
      upload();
    };
    return (
      <React.Fragment>
        <hr className='m-0' />
        <div className='py-2'>
          <ValidationStatus validationStatus={validationStatus} />
          <span className='ml-1'>
            {t('upload_validation_experimentListCheck')}
          </span>
          {runFolderValidationStatus !== undefined && (
            <span className='ml-2'>
              <i className='fas fa-chevron-right fcolor--text-label' />
              <span className='ml-2'>
                <ValidationStatus
                  validationStatus={runFolderValidationStatus}
                />
              </span>
              <span className='ml-2'>
                {t('upload_validation_checkRunFolder')}
              </span>
            </span>
          )}
          {validationStatus === ValidationState.failed && (
            <span className='bg-color--error fcolor--global-white px-2 py-1 ml-2 font-weight-bold'>
              {t('upload_validation_failedVerification', { uppercase: true })}
            </span>
          )}
          {validationStatus === ValidationState.passed && (
            <React.Fragment>
              <span className='success-box px-2 py-1 ml-3'>
                {t('upload_validation_passedVerification', { uppercase: true })}
              </span>
              <button
                className='btn btn-sm btn-primary ml-3'
                onClick={onUpload}
                disabled={uploading}
              >
                {t('upload_review_startUpload', { uppercase: true })}
              </button>
            </React.Fragment>
          )}
          {uploading && (
            <span className='ml-2'>{t('upload_uploadStarting')}</span>
          )}
          {uploadCancelled && (
            <span className='ml-2'>
              {t('upload_validation_uploadCancelled')}
            </span>
          )}
        </div>
        {errors.map((error) => (
          <UploadError errorCode={error.code} key={error.code} />
        ))}
        <ConfirmModalDialog
          show={showDialog}
          title={t('upload_confirmation')}
          confirm={confirmedUpload}
          cancel={() => setShowDialog(false)}
          cancelLabel={t('common_cancel', { uppercase: true })}
          confirmLabel={t('upload_readyToUpload', { uppercase: true })}
          confirmButtonClass={'btn-primary confirm-btn'}
          modalSize={'lg'}
          cls='upload-validating-modal'
        >
          <Container fluid className='uv-modal-body'>
            <Row>
              <Col className='p-0'>
                <div className='title'>
                  {t('upload_uploadChecklist_heading')}
                </div>
              </Col>
            </Row>
            <Row className='pt-3 pb-2'>
              {uploadChecklistSet.map((item, index) => (
                <Col key={index} className={item.columnCls}>
                  {<item.img className='mx-5 my-1' />}
                  <p className='p-2 m-0 font-size-14 checklist-title'>
                    <span className='font-weight-bold fcolor--primary-brand'>
                      {index + 1}.
                    </span>
                    &nbsp;
                    {t(item.titleToken)}
                  </p>
                </Col>
              ))}
            </Row>
          </Container>
        </ConfirmModalDialog>
      </React.Fragment>
    );
  };
export default Validating;
